import { isJust, toMaybe } from "../../maybe";
import { isTauri } from "../../runtime";
import { isCloudRoot } from "../projectRoot";
import { createDiskBackend } from "./diskBackend";
import type { ProjectBackend } from "./types";

const cache = new Map<string, ProjectBackend>();

/** Clear cached backend for a root (e.g. after cloud project reset). */
export function clearProjectBackendCache(rootPath?: string): void {
  const rootM = toMaybe(rootPath);
  if (isJust(rootM) && rootM.value !== "") cache.delete(rootM.value);
  else cache.clear();
}

/**
 * Single entry point for project storage + git on desktop.
 * Browser editor uses cloud HTTP APIs via `cloudEditorSession` instead.
 */
export async function getProjectBackend(
  rootPath: string,
): Promise<ProjectBackend> {
  if (isTauri() === false) {
    throw new Error(
      "Browser editor uses cloud APIs directly; disk backend is desktop-only",
    );
  }
  if (isCloudRoot(rootPath)) {
    throw new Error(
      "Cloud projects use HTTP APIs; disk backend is for local paths only",
    );
  }
  const hitM = toMaybe(cache.get(rootPath));
  if (isJust(hitM)) return hitM.value;
  const backend = await createDiskBackend(rootPath);
  cache.set(rootPath, backend);
  return backend;
}

export function projectRootKind(rootPath: string): "disk" | "cloud" | "demo" {
  if (rootPath.startsWith("cloud://")) return "cloud";
  if (rootPath.startsWith("demo://")) return "demo";
  return "disk";
}
